import React, { useState, useEffect, useCallback } from 'react';
import useAuth from '../contexts/useAuth';
import { FlatConversationsTable } from '../components/FlatConversationsTable';
import { dynamoDBService, FlattenedConversation } from '../utils/dynamodb';
import { exportFlattenedConversationsToCSV } from '../utils/csvExport';
import { RefreshCw } from 'lucide-react';
import { Layout } from '../components/Layout';
import { ADMIN_USERS } from '../utils/constants';

export const ConversationsPage: React.FC = () => {
  const { getCredentials, user } = useAuth();
  // Sidebar state
  const [conversations, setConversations] = useState<FlattenedConversation[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const credentials = await getCredentials();
      await dynamoDBService.initializeClient(credentials);

      // For admin users, show all conversations; for regular users, show only their own
      const username = user?.username;
      if (!username) {
        throw new Error('User not authenticated');
      }

      // Check if user is admin to decide whether to show all conversations
      const isAdmin = username && ADMIN_USERS.includes(username);
      const data = await dynamoDBService.getAllConversations(
        isAdmin ? '*' : username,
      );
      setConversations(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  }, [getCredentials, user?.username]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const handleExportCSV = () => {
    try {
      exportFlattenedConversationsToCSV(conversations);
    } catch (err: any) {
      setError('Failed to export CSV: ' + err.message);
    }
  };

  console.log(conversations.map((c) => c.pk));

  return (
    <Layout sidebar={true}>
      <div className="flex flex-col h-full overflow-hidden px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4 flex justify-between items-start flex-shrink-0">
            <p className="text-sm text-red-800">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        )}
        <div className="flex-1 min-h-0">
          <FlatConversationsTable
            conversations={conversations}
            isLoading={isLoading}
            onExportCSV={handleExportCSV}
            RefreshButton={
              <button
                onClick={loadConversations}
                disabled={isLoading}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
                />
                Refresh
              </button>
            }
          />
        </div>
      </div>
    </Layout>
  );
};
